import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { usePermission } from '../store/auth';

/**
 * WHERE EACH FIELD USER WORKS, resolved once for the whole app.
 *
 * A staff row carries no geography — a territory assignment does, and the assignment's meaning
 * ("Pune", "Maharashtra") lives in its ANCESTORS. So the two masters are fetched flat and joined
 * here: every active, non-coverage assignment is walked up the tree (beat → district → state → zone)
 * and every node on that path is credited to the rep. A rep on three beats of one district therefore
 * matches that district AND its state, which is what anyone picking "Maharashtra" expects.
 *
 * Coverage assignments are deliberately excluded, matching `GET /territory-assignments` itself: a
 * stand-in covering someone's leave does not WORK that area, and counting them there would quietly
 * widen every territory filter during every leave.
 *
 * Nodes are addressed by **id, never by name** — two states can hold a district of the same name, and
 * a name-keyed filter would silently merge them. Labels are for the reader; ids are the filter.
 */

export type TerritoryLevel = 'zone' | 'state' | 'district' | 'beat';

export interface TerritoryNode { id: string; parentId: string | null; level: TerritoryLevel; name: string }

export interface TerritoryOption { value: string; label: string; disabled?: boolean }

/** The three levels a picker can hold. `null` at a level means "all of them". */
export interface TerritoryPick { state: string | null; district: string | null; beat: string | null }
export const NO_TERRITORY: TerritoryPick = { state: null, district: null, beat: null };
/** The DEEPEST node chosen is the filter — everything above it is already implied by the tree. */
export const territoryIdOf = (t: TerritoryPick) => t.beat ?? t.district ?? t.state ?? null;

export interface StaffTerritories {
  /** False when the signed-in user may not read the territory masters — hide the filters entirely. */
  available: boolean;
  loading: boolean;
  nodes: Map<string, TerritoryNode>;
  /** A node's display name, or '' when it is unknown (a stale bookmark, a deleted territory). */
  nameOf: (id?: string | null) => string;
  /**
   * Options for one level, as `Name (staff count)`. EVERY node at the level is listed so the
   * geography stays visible, but one with nobody in it is DISABLED and sorted last: you can see
   * where you have no staff without being able to filter yourself into an empty table.
   */
  optionsAt: (level: TerritoryLevel, parentId: string | null) => TerritoryOption[];
  /** True when the rep works at or under `territoryId` (`null` ⇒ no filter, everyone matches). */
  matches: (userId: string | number | null | undefined, territoryId: string | null) => boolean;
  /** The ids a rep works at or under, split by level — the shape a facet filter matches against. */
  levelIdsOf: (userId: string | number | null | undefined) => { state: string[]; district: string[]; beat: string[] };
  /** Short "works in …" caption: the deepest nodes they are actually assigned to. */
  labelFor: (userId: string | number | null | undefined, max?: number) => string;
  /**
   * The rep's BOOK as nodes rather than a joined string — so a caller can colour each one by its
   * level instead of printing "Katol · Maharashtra" and leaving the reader to know which is which.
   *
   * Deepest first, for the same reason `labelFor` sorts that way: a beat says more about a rep's day
   * than the state containing it. This is a LIST, not a path — a rep can hold three unrelated beats,
   * and one of them is not an ancestor of another.
   */
  booksOf: (userId: string | number | null | undefined) => TerritoryNode[];
}

const EMPTY_LEVELS = { state: [] as string[], district: [] as string[], beat: [] as string[] };

/**
 * @param staffIds When given, the option counts are staff **from this set** — so the numbers describe
 *   the list on screen (the field staff) rather than every user in the database.
 */
export function useStaffTerritories(staffIds?: Array<string | number>): StaffTerritories {
  const canRead = usePermission('TERRITORY.VIEW');
  // Both masters are small, change rarely and are read by several screens — cached for the session.
  const { data: territories, isLoading: l1 } = useQuery({
    queryKey: ['territories-flat'],
    queryFn: () => request<any[]>({ url: '/territories' }),
    enabled: canRead,
    staleTime: 10 * 60_000,
  });
  const { data: assignments, isLoading: l2 } = useQuery({
    queryKey: ['territory-assignments-all'],
    queryFn: () => request<any[]>({ url: '/territory-assignments' }),
    enabled: canRead,
    staleTime: 10 * 60_000,
  });

  const staffKey = staffIds ? staffIds.map(String).sort().join(',') : '';

  return useMemo(() => {
    const nodes = new Map<string, TerritoryNode>(
      (territories ?? []).map((t: any) => [String(t.id), {
        id: String(t.id),
        parentId: t.parentId != null ? String(t.parentId) : null,
        level: t.level as TerritoryLevel,
        name: String(t.name ?? ''),
      }]),
    );

    /** A node and all its ancestors, deepest first. Depth-guarded: a cyclic parent chain in the
     *  master data would otherwise hang the page rather than merely look wrong. */
    const chain = (id: string): TerritoryNode[] => {
      const out: TerritoryNode[] = [];
      let cur = nodes.get(String(id));
      for (let hop = 0; cur && hop < 8; hop++) { out.push(cur); cur = cur.parentId ? nodes.get(cur.parentId) : undefined; }
      return out;
    };

    const wanted = staffIds ? new Set(staffIds.map(String)) : null;
    // userId → every node id they work at or under; plus the nodes they are directly assigned to.
    const scopeOf = new Map<string, Set<string>>();
    const assignedOf = new Map<string, TerritoryNode[]>();
    // node id → the staff working at or under it, for the option counts.
    const staffAt = new Map<string, Set<string>>();

    for (const a of assignments ?? []) {
      const uid = String(a.userId);
      if (wanted && !wanted.has(uid)) continue;
      const path = chain(String(a.territoryId));
      if (!path.length) continue; // an assignment to a territory that no longer exists
      const scope = scopeOf.get(uid) ?? new Set<string>();
      scopeOf.set(uid, scope);
      const assigned = assignedOf.get(uid) ?? [];
      assignedOf.set(uid, assigned);
      if (!assigned.some((n) => n.id === path[0].id)) assigned.push(path[0]);
      for (const n of path) {
        scope.add(n.id);
        const set = staffAt.get(n.id) ?? new Set<string>();
        staffAt.set(n.id, set);
        set.add(uid);
      }
    }

    // Deepest first — a beat says more about a rep's day than the state containing it. One
    // definition, so the caption text and the coloured chips can never list different territories.
    const BOOK_ORDER: TerritoryLevel[] = ['beat', 'district', 'state', 'zone'];
    const booksOf = (userId: string | number | null | undefined): TerritoryNode[] => {
      if (userId == null) return [];
      return [...(assignedOf.get(String(userId)) ?? [])]
        .sort((a, b) => BOOK_ORDER.indexOf(a.level) - BOOK_ORDER.indexOf(b.level) || a.name.localeCompare(b.name));
    };

    const optionsAt = (level: TerritoryLevel, parentId: string | null): TerritoryOption[] =>
      [...nodes.values()]
        .filter((n) => n.level === level && (parentId === null || n.parentId === parentId))
        .map((n) => {
          const count = staffAt.get(n.id)?.size ?? 0;
          return { value: n.id, label: `${n.name} (${count})`, disabled: count === 0 };
        })
        // Places with nobody in them fall to the bottom rather than disappearing, so the geography
        // is still visible and the useful choices are the ones you land on first.
        .sort((a, b) => Number(a.disabled) - Number(b.disabled) || a.label.localeCompare(b.label));

    const levelIdsOf = (userId: string | number | null | undefined) => {
      if (userId == null) return EMPTY_LEVELS;
      const scope = scopeOf.get(String(userId));
      if (!scope) return EMPTY_LEVELS;
      const out = { state: [] as string[], district: [] as string[], beat: [] as string[] };
      for (const id of scope) {
        const n = nodes.get(id);
        if (n && (n.level === 'state' || n.level === 'district' || n.level === 'beat')) out[n.level].push(id);
      }
      return out;
    };

    return {
      available: canRead,
      loading: l1 || l2,
      nodes,
      nameOf: (id) => (id ? nodes.get(String(id))?.name ?? '' : ''),
      optionsAt,
      matches: (userId, territoryId) => {
        if (!territoryId) return true;
        if (userId == null) return false;
        return scopeOf.get(String(userId))?.has(String(territoryId)) ?? false;
      },
      levelIdsOf,
      labelFor: (userId, max = 2) => booksOf(userId).slice(0, max).map((n) => n.name).join(' · '),
      booksOf,
    };
  }, [territories, assignments, l1, l2, canRead, staffKey]); // eslint-disable-line react-hooks/exhaustive-deps
}
